import { me } from '../api/endpoints.ts';
import { currentStrings, onLangChange } from '../i18n/index.ts';
import { addressBookStore, forgetAccountAddresses, loadAddresses, refreshQuote, storefrontScope } from './address-book.ts';
import { adoptCart, applyStock, cartStore, forgetAvailability, orderItems, releaseCart } from './cart.ts';
import { guestBirthDate, setGuestBirthDate, setMirroredBirthDate } from './gates.ts';
import { runLaunch } from './launch.ts';
import { loadPaymentConfig, loadSavedCards, savedCardsStore } from './payment.ts';
import { sharePushLang, startPush, syncPushSubscription } from './push.ts';
import { restoreSession, sessionStore, setBirthDate } from './session.ts';
import { loadFavorites, resetFavorites, resetNotifications, stockStore } from './stores.ts';
import { notify } from './toasts.ts';
import { setWatcherForeground, setWatcherScope, setWatcherSignedIn } from './watcher.ts';

/**
 * Связи между хранилищами, которые не принадлежат ни одному экрану: что происходит при входе,
 * выходе, смене адреса и правке корзины.
 */

const CART_REPORT_DELAY_MS = 1_500;

/** Корзина, адреса, избранное и уведомления принадлежат человеку, а не устройству. */
function forgetPerson(): void {
  releaseCart();
  forgetAccountAddresses();
  resetFavorites();
  resetNotifications();
}

export function startWiring(): void {
  let profileId: string | null = null;
  let revocations = sessionStore.get().revocations;
  let wasSignedIn = false;

  const onSession = () => {
    const state = sessionStore.get();
    const signedIn = state.profile !== null;

    // Корзина сверяется с хозяином на каждом входе, включая первый после запуска.
    if (state.profile && state.profile.id !== profileId) adoptCart(state.profile.id);
    profileId = state.profile?.id ?? null;

    // Зеркало даты рождения для корзины. Дата, названная гостем, уходит в профиль при первом входе.
    const guestDate = guestBirthDate();
    setMirroredBirthDate(state.profile?.birthDate ?? guestDate);
    if (state.profile && !state.profile.birthDate && guestDate) {
      setGuestBirthDate(null);
      void setBirthDate(guestDate).catch(() => setGuestBirthDate(guestDate));
    }

    if (state.status !== 'unknown') setWatcherSignedIn(signedIn);

    if (signedIn && !wasSignedIn) {
      // Вход: адрес гостя уходит в аккаунт, грузятся адреса и избранное.
      void loadAddresses().catch(() => undefined);
      void loadFavorites();
      void syncPushSubscription();
    } else if (!signedIn && wasSignedIn) {
      forgetPerson();
    }
    wasSignedIn = signedIn;

    // Сервер отверг токен: это бывает и на запуске, поэтому данные человека стираются здесь,
    // а причина называется вслух.
    if (state.revocations !== revocations) {
      revocations = state.revocations;
      forgetPerson();
      notify(currentStrings().sessionExpired);
    }
  };
  sessionStore.subscribe(onSession);

  // Смена адреса меняет склад: лента событий переподключается, прежние остатки не годятся.
  let scope = storefrontScope(addressBookStore.get());
  setWatcherScope(scope);
  addressBookStore.subscribe(() => {
    const next = storefrontScope(addressBookStore.get());
    if (next === scope) return;
    scope = next;
    forgetAvailability();
    setWatcherScope(next);
  });

  // Остаток из ленты идёт в кеш корзины: её ограничения и «нет в наличии» без запроса.
  stockStore.subscribe(() => {
    const event = stockStore.get().last;
    if (event) applyStock(event.productId, event.availableQty);
  });

  // Снимок корзины уходит на сервер после каждой правки, ответа никто не ждёт: корзина живёт
  // в браузере, сервер её только смотрит. Пустая тоже уходит: доска заказов снимает её.
  let lines = cartStore.get().lines;
  let reportTimer: ReturnType<typeof setTimeout> | undefined;
  cartStore.subscribe(() => {
    const state = cartStore.get();
    if (state.lines === lines) return;
    lines = state.lines;
    if (sessionStore.get().profile === null) return;
    clearTimeout(reportTimer);
    reportTimer = setTimeout(() => {
      void me.reportCart(addressBookStore.get().selectedId, orderItems(cartStore.get())).catch(() => undefined);
    }, CART_REPORT_DELAY_MS);
  });

  // Тап по push ведёт туда же, куда ссылка запуска; язык уведомлений следует за интерфейсом.
  startPush((query) => runLaunch(new URLSearchParams(query)));
  onLangChange(sharePushLang);

  // Поток событий живёт, пока вкладка на экране.
  const onVisibility = () => setWatcherForeground(document.visibilityState === 'visible');
  document.addEventListener('visibilitychange', onVisibility);
  onVisibility();

  // Сохранённые карты читаются после конфигурации оплаты (она говорит, есть ли они) и при входе.
  void loadPaymentConfig().then(() => {
    if (sessionStore.get().profile !== null) void loadSavedCards();
  });
  sessionStore.subscribe(() => {
    if (sessionStore.get().profile !== null && !savedCardsStore.get().loaded) void loadSavedCards();
  });
  void restoreSession().then(() => {
    // Точка гостя с прошлого запуска: её тариф нужен витрине до входа.
    if (sessionStore.get().profile === null && addressBookStore.get().guest) void refreshQuote().catch(() => undefined);
  });
}
